import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Unlock, Lock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { formatClock } from "@/components/Countdown";

const EMPTY = { name: "", description: "", icon: "flag", color: "#FF9933", order: 1, question_count: 10, question_timer: 20, active: true };

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [config, setConfig] = useState(null);

  const load = async () => {
    try {
      const [{ data }, cfg] = await Promise.all([api.get("/admin/categories"), api.get("/public/config")]);
      setCats(data.categories);
      setConfig(cfg.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing === "new") await api.post("/admin/categories", { ...form, order: Number(form.order), question_count: Number(form.question_count), question_timer: Number(form.question_timer) });
      else await api.patch(`/admin/categories/${editing.id}`, { ...form, order: Number(form.order), question_count: Number(form.question_count), question_timer: Number(form.question_timer) });
      toast.success("Category saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and ALL its questions and attempts?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleUnlock = async (c) => {
    try {
      const { data } = await api.post(`/admin/categories/${c.id}/unlock`);
      toast.success(data.manual_unlock ? `${c.name} manually unlocked` : `${c.name} back on schedule`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const unlockAt = (order) => {
    const sch = config?.schedule?.find((s) => s.order === order);
    return sch ? formatClock(sch.unlock_at) : "—";
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Categories</h1>
          <p className="text-sm text-slate-500 mt-1">Unlock times follow the competition schedule (start + index × interval).</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY, order: cats.length + 1 }); setEditing("new"); }} data-testid="add-category-btn" className="px-5 py-2.5 rounded-lg bg-[#FF9933] text-white text-sm font-bold hover:bg-[#E6892E] transition-colors inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add Category</button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-bold tracking-[0.15em] uppercase text-slate-400 border-b border-slate-200">
              <th className="text-left px-5 py-3">Order</th><th className="text-left px-5 py-3">Category</th><th className="text-left px-5 py-3">Unlocks</th><th className="text-left px-5 py-3">Questions</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cats.map((c) => (
              <tr key={c.id} data-testid={`category-row-${c.order}`}>
                <td className="px-5 py-3 font-timer font-bold text-slate-500">{String(c.order).padStart(2, "0")}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg" style={{ background: c.color }} />
                    <div><div className="font-semibold text-slate-800">{c.name}</div><div className="text-xs text-slate-400">{c.description}</div></div>
                  </div>
                </td>
                <td className="px-5 py-3 text-xs font-semibold text-slate-500">{unlockAt(c.order)} IST {c.manual_unlock && <span className="text-[#138808] font-bold">(manual)</span>}</td>
                <td className="px-5 py-3 text-xs font-semibold text-slate-600">{c.actual_questions}/{c.question_count}</td>
                <td className="px-5 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${c.active ? "bg-[#138808]/10 text-[#138808]" : "bg-slate-100 text-slate-400"}`}>{c.active ? "Active" : "Disabled"}</span></td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button onClick={() => toggleUnlock(c)} data-testid={`unlock-cat-${c.order}`} title="Manual unlock toggle" className={`p-2 rounded-lg transition-colors ${c.manual_unlock ? "bg-[#138808]/10 text-[#138808]" : "text-slate-400 hover:bg-slate-100"}`}>{c.manual_unlock ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}</button>
                    <button onClick={() => { setForm({ name: c.name, description: c.description, icon: c.icon, color: c.color, order: c.order, question_count: c.question_count, question_timer: c.question_timer || 20, active: c.active }); setEditing(c); }} data-testid={`edit-cat-${c.order}`} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(c)} data-testid={`delete-cat-${c.order}`} className="p-2 rounded-lg text-red-400 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="category-form-modal">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New Category" : "Edit Category"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Name</label><Input data-testid="cat-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Description</label><Input data-testid="cat-description" className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Order</label><Input data-testid="cat-order" type="number" className="mt-1.5" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
              <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Questions</label><Input data-testid="cat-qcount" type="number" className="mt-1.5" value={form.question_count} onChange={(e) => setForm({ ...form, question_count: e.target.value })} /></div>
              <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Timer (s)</label><Input data-testid="cat-timer" type="number" className="mt-1.5" value={form.question_timer} onChange={(e) => setForm({ ...form, question_timer: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Color</label><Input data-testid="cat-color" type="color" className="mt-1.5 h-10" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
              <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Icon (lucide name)</label><Input data-testid="cat-icon" className="mt-1.5" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></div>
            </div>
            <button onClick={save} data-testid="cat-save" className="w-full py-3 rounded-lg bg-[#003366] text-white font-bold hover:bg-[#00284f] transition-colors">Save Category</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
